import type { UIMessage } from "ai"
import { projectFromArtifact, type Artifact, type BlockArtifact, type QuiltArtifact } from "./artifact"
import { DEFAULT_BLOCK_COLORS, renderBlock } from "./core/block_svg"
import { fullQuiltSvg } from "./core/layout"
import { EMPTY_TOOL_OUTPUTS, openDB, PINS_STORE, type LoomDoc } from "./storage"

// Pin records live in IndexedDB keyed by a content hash of the artifact.
// Re-pinning an identical design is a no-op (overwrite with same id, same
// content) — the user can't accidentally accumulate duplicates of the same
// thing. The hash also doubles as a cheap "is this pinned?" lookup.

export interface PinRecord {
  id: string
  pinnedAt: string  // ISO timestamp of first pinning
  artifact: Artifact
}

// Stable JSON serialization with deterministic key ordering. JSON.stringify
// preserves insertion order for object keys, so without sorting two
// equivalent artifacts could hash differently if their fabrics arrays were
// re-ordered. We sort keys recursively before hashing.
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value)
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]"
  }
  const keys = Object.keys(value as Record<string, unknown>).sort()
  const entries = keys.map(
    (k) => JSON.stringify(k) + ":" + stableStringify((value as Record<string, unknown>)[k]),
  )
  return "{" + entries.join(",") + "}"
}

async function hashArtifact(artifact: Artifact): Promise<string> {
  const buf = new TextEncoder().encode(stableStringify(artifact))
  const digest = await crypto.subtle.digest("SHA-256", buf)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .slice(0, 16)
}

export async function pinIdFor(artifact: Artifact): Promise<string> {
  return hashArtifact(artifact)
}

export async function listPins(): Promise<PinRecord[]> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PINS_STORE, "readonly")
    const req = tx.objectStore(PINS_STORE).getAll()
    req.onsuccess = () => {
      const pins: PinRecord[] = req.result ?? []
      pins.sort((a, b) => b.pinnedAt.localeCompare(a.pinnedAt))
      resolve(pins)
    }
    req.onerror = () => reject(req.error)
  })
}

export async function isPinned(id: string): Promise<boolean> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PINS_STORE, "readonly")
    const req = tx.objectStore(PINS_STORE).getKey(id)
    req.onsuccess = () => resolve(req.result !== undefined)
    req.onerror = () => reject(req.error)
  })
}

export async function addPin(artifact: Artifact): Promise<PinRecord> {
  const id = await hashArtifact(artifact)
  const existing = await loadPin(id)
  if (existing) return existing
  const record: PinRecord = {
    id,
    pinnedAt: new Date().toISOString(),
    artifact,
  }
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PINS_STORE, "readwrite")
    tx.objectStore(PINS_STORE).put(record, id)
    tx.oncomplete = () => resolve(record)
    tx.onerror = () => reject(tx.error)
  })
}

export async function removePin(id: string): Promise<void> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PINS_STORE, "readwrite")
    tx.objectStore(PINS_STORE).delete(id)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

async function loadPin(id: string): Promise<PinRecord | null> {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PINS_STORE, "readonly")
    const req = tx.objectStore(PINS_STORE).get(id)
    req.onsuccess = () => resolve((req.result as PinRecord | undefined) ?? null)
    req.onerror = () => reject(req.error)
  })
}

// Type narrowers used by the scrapbook UI.
export function isBlockPin(pin: PinRecord): pin is PinRecord & { artifact: BlockArtifact } {
  return pin.artifact.kind === "block"
}

export function isQuiltPin(pin: PinRecord): pin is PinRecord & { artifact: QuiltArtifact } {
  return pin.artifact.kind === "quilt"
}

// Materialize an artifact into a fresh LoomDoc — the active session a
// "New project from this …" action opens into. Block artifacts yield a
// block-kind doc with an empty layout (rows=cols=0); quilt artifacts yield
// a quilt-kind doc with the layout the artifact captured. The kind label
// travels through the session so re-saving preserves it.
//
// The synthetic opening message is critical: without it, the LLM sees an
// empty transcript and assumes the project is empty too — even though the
// Project state has fabrics + block + colorways pre-loaded. Seeding a
// completed render_block / render_quilt tool call:
//   1) shows the user the design immediately on open, and
//   2) tells the LLM "this project already has stuff in it" so the next
//      user prompt is interpreted in context.
//
// Used by both pin opens (scrapbook) and direct artifact opens (the
// fullscreen viewer's "New project" CTA in the chat) — same operation,
// same seeded transcript shape.
export function loomDocFromArtifact(artifact: Artifact): LoomDoc {
  const project = projectFromArtifact(artifact)
  const suffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  const toolCallId = `artifact-open-${suffix}`
  const messageId = `artifact-msg-${suffix}`

  if (artifact.kind === "block") {
    const colors = { ...DEFAULT_BLOCK_COLORS, ...artifact.colorway }
    const svg = renderBlock(artifact.block, 60, colors)
    const transcript: UIMessage[] = [{
      id: messageId,
      role: "assistant",
      parts: [
        { type: "text", text: "Loaded from scrapbook." },
        {
          type: "dynamic-tool",
          toolName: "render_block",
          toolCallId,
          state: "output-available",
          input: {},
          output: { ok: true },
        },
      ],
    }]
    return {
      project,
      transcript,
      toolOutputs: {
        ...EMPTY_TOOL_OUTPUTS,
        blockSvgs: { [toolCallId]: { svg, artifact } },
      },
      schemaVersion: 1,
      kind: "block",
    }
  }

  // Quilt artifact
  const svg = fullQuiltSvg(project, 48)
  const transcript: UIMessage[] = [{
    id: messageId,
    role: "assistant",
    parts: [
      { type: "text", text: "Loaded from scrapbook." },
      {
        type: "dynamic-tool",
        toolName: "render_quilt",
        toolCallId,
        state: "output-available",
        input: {},
        output: { ok: true },
      },
    ],
  }]
  return {
    project,
    transcript,
    toolOutputs: {
      ...EMPTY_TOOL_OUTPUTS,
      quiltSvgs: { [toolCallId]: { svg, artifact } },
    },
    schemaVersion: 1,
    kind: "quilt",
  }
}

// Backwards-compatible alias for callers that work in pin-record terms
// (the scrapbook). Same behavior; just unwraps pin.artifact.
export function loomDocFromPin(pin: PinRecord): LoomDoc {
  return loomDocFromArtifact(pin.artifact)
}
